'use client';

import { useEffect, useState } from 'react';

import { cn } from '@ps/ui/utils';
import { ArrowUp } from 'lucide-react';

import { BIO_SCROLL_ROOT_SELECTOR } from '../../linking/functions/linkNodePath';

/** Reveal the button once the scroller has moved past roughly one screenful. */
const REVEAL_THRESHOLD_PX = 400;

/**
 * "Back to top" control for the bio scroller, shared by the visitor and editable
 * surfaces (both render through {@link ShellFrame}). Mounted in the frame's
 * `floating` slot so it sits at the `<main>` level OUTSIDE the scroller — pinned
 * via `absolute` (not `position: fixed`) so it stays put while the inner
 * `[data-bio-scroll-root]` scroller moves, and works both full-viewport (visitor)
 * and inside the bounded dashboard preview (editable).
 *
 *   Desktop (≥lg): docked in the RIGHT gutter beside the 480px card's right edge
 *   (`lg:left-1/2 lg:translate-x-[256px]` = half the card + a 16px gap, mirroring
 *   BioStyleButton's `-translate-x-[240px]` left dock).
 *
 *   Mobile (<lg): a FAB in the bottom-right corner (`right-5`), where there's no
 *   gutter to dock into.
 *
 * Neutral dark-translucent chrome (white icon + ring + shadow + blur) reads on
 * any creator background, so there's no per-theme color to derive.
 */
export function BioScrollToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const root = document.querySelector<HTMLElement>(BIO_SCROLL_ROOT_SELECTOR);
    if (root == null) return;

    const sync = () => setVisible(root.scrollTop > REVEAL_THRESHOLD_PX);
    sync();
    root.addEventListener('scroll', sync, { passive: true });
    return () => root.removeEventListener('scroll', sync);
  }, []);

  const scrollToTop = () => {
    const root = document.querySelector<HTMLElement>(BIO_SCROLL_ROOT_SELECTOR);
    root?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <button
      type="button"
      onClick={scrollToTop}
      aria-label="Back to top"
      className={cn(
        'absolute bottom-5 right-5 z-30 flex h-11 w-11 items-center justify-center rounded-full',
        // Hide while a detail aside (View Series / View Course / product
        // detail) is open — the FAB's stacking context painted it over
        // the aside panel (QA). `:has` on body reacts to any open aside
        // without wiring state through the tree.
        '[body:has([data-detail-aside])_&]:hidden',
        'bg-black/55 text-white shadow-lg ring-1 ring-white/25 backdrop-blur-sm',
        'transition duration-200 hover:bg-black/70',
        'lg:bottom-6 lg:left-1/2 lg:right-auto lg:translate-x-[256px]',
        visible
          ? 'pointer-events-auto opacity-100'
          : 'pointer-events-none opacity-0',
      )}
    >
      <ArrowUp className="h-5 w-5" aria-hidden />
    </button>
  );
}
